'use client';
import React, { useState, useEffect } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { X } from 'lucide-react';
import PostProductModal from './PostProductModal';
import SellerAuthGate from './SellerAuthGate';
import { notificationStore } from '@/lib/notificationStore';
import { CreateMenu } from './CreateMenu';
import { useAuth } from '@/contexts/AuthContext';

// ─── Right Menu Modules (realistic icon images) ────────────────────────────────
const rightModules = [
  { id: 'explore', label: 'Explore', img: '/assets/images/menu-explore.png', href: '/search' },
  { id: 'hotels', label: 'Hotels', img: '/assets/images/services-hotels.png', href: '/hotels' },
  { id: 'transport', label: 'Transport', img: '/assets/images/services-transport.png', href: '/transport' },
  { id: 'food', label: 'Food', img: '/assets/images/services-food.png', href: '/food' },
  { id: 'pharmacy', label: 'Pharmacy', img: '/assets/images/services-pharmacy.png', href: '/pharmacy' },
  { id: 'events', label: 'Events', img: '/assets/images/services-events.png', href: '/events' },
  { id: 'videos', label: 'Videos', img: '/assets/images/services-videos.png', href: '/videos' },
  { id: 'auto-parts', label: 'Auto Parts', img: '/assets/images/services-auto-parts.png', href: '/auto-parts' },
  { id: 'machinery', label: 'Machinery', img: '/assets/images/services-machinery.png', href: '/machinery' },
  { id: 'sweden', label: 'Sweden Store', img: '/assets/images/services-sweden-store.png', href: '/sweden-store' },
  { id: 'uk', label: 'UK Store', img: '/assets/images/services-uk-store.png', href: '/uk-store' },
  { id: 'china', label: 'China Offers', img: '/assets/images/services-china-offers.png', href: '/china-offers' },
  { id: 'profile', label: 'Profile', img: '/assets/images/services-profile.png', href: '/profile' },
  { id: 'settings', label: 'Settings', img: '/assets/images/services-settings.png', href: '/profile' },
  { id: 'news', label: 'News', img: '/assets/images/services-news.png', href: '/news' },
  { id: 'credit-market', label: 'Credits', img: '/assets/images/services-credits.png', href: '/credit-marketplace' },
];

// ─── Left Menu Modules — split by auth state ──────────────────────────────────
const publicLeftModules = [
  { id: 'home', label: 'Home', img: '/assets/images/menu-home.png', href: '/' },
  { id: 'explore', label: 'Explore', img: '/assets/images/menu-explore.png', href: '/search' },
  { id: 'classifieds', label: 'Classifieds', img: '/assets/images/menu-classifieds.png', href: '/classifieds' },
  { id: 'jobs', label: 'Jobs', img: '/assets/images/menu-jobs.png', href: '/jobs' },
  { id: 'dating', label: 'Dating', img: '/assets/images/menu-dating.png', href: '/dating' },
];

const privateLeftModules = [
  { id: 'my-dashboard', label: 'My Dashboard', img: '/assets/images/menu-dashboard.png', href: '/user-dashboard' },
  { id: 'seller-dashboard', label: 'Seller Hub', img: '/assets/images/menu-seller-hub.png', href: '/seller-dashboard' },
  { id: 'chat', label: 'Chat', img: '/assets/images/menu-chat.png', href: '/chat', badge: 3 },
  { id: 'wallet', label: 'Wallet', img: '/assets/images/menu-wallet.png', href: '/wallet' },
  { id: 'bookings', label: 'Bookings', img: '/assets/images/menu-bookings.png', href: '/bookings', badge: 2 },
  { id: 'alerts', label: 'Alerts', img: '/assets/images/menu-alerts.png', href: '/notifications', badge: 0 },
];

const leftModulesBase = [
  { id: 'home', label: 'Home', img: '/assets/images/menu-home.png', href: '/' },
  { id: 'my-dashboard', label: 'My Dashboard', img: '/assets/images/menu-dashboard.png', href: '/user-dashboard' },
  { id: 'seller-dashboard', label: 'Seller Hub', img: '/assets/images/menu-seller-hub.png', href: '/seller-dashboard' },
  { id: 'chat', label: 'Chat', img: '/assets/images/menu-chat.png', href: '/chat', badge: 3 },
  { id: 'explore', label: 'Explore', img: '/assets/images/menu-explore.png', href: '/search' },
  { id: 'classifieds', label: 'Classifieds', img: '/assets/images/menu-classifieds.png', href: '/classifieds' },
  { id: 'jobs', label: 'Jobs', img: '/assets/images/menu-jobs.png', href: '/jobs' },
  { id: 'dating', label: 'Dating', img: '/assets/images/menu-dating.png', href: '/dating' },
  { id: 'wallet', label: 'Wallet', img: '/assets/images/menu-wallet.png', href: '/wallet' },
  { id: 'bookings', label: 'Bookings', img: '/assets/images/menu-bookings.png', href: '/bookings', badge: 2 },
  { id: 'alerts', label: 'Alerts', img: '/assets/images/menu-alerts.png', href: '/notifications', badge: 0 },
];

export default function BottomNav() {
  const pathname = usePathname();
  const router = useRouter();
  const { user, loading: authLoading } = useAuth();
  const isAuthenticated = !authLoading && !!user;

  const [leftOpen, setLeftOpen] = useState(false);
  const [rightOpen, setRightOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [postOpen, setPostOpen] = useState(false);
  const [postCategory, setPostCategory] = useState<string | null>(null);
  const [alertBadge, setAlertBadge] = useState(notificationStore.getUnreadCount());
  const [showAuthGate, setShowAuthGate] = useState(false);
  const [pendingCategory, setPendingCategory] = useState<string | null>(null);

  useEffect(() => {
    const unsub = notificationStore.subscribe(() => {
      setAlertBadge(notificationStore.getUnreadCount());
    });
    return unsub;
  }, []);

  // Role-aware left modules: public users see only public modules
  const roleLeftModules = isAuthenticated
    ? leftModulesBase.map(m => m.id === 'alerts' ? { ...m, badge: alertBadge } : m)
    : publicLeftModules;

  const leftModules = roleLeftModules;

  const closeAll = () => { setLeftOpen(false); setRightOpen(false); setCreateOpen(false); setPostOpen(false); };

  const handleModuleNav = (href: string) => {
    closeAll();
    router.push(href);
  };

  const isActive = (href: string) => {
    if (href === '/') return pathname === '/' || pathname === '/home-screen';
    return pathname === href || pathname.startsWith(href + '/');
  };

  const anyOpen = leftOpen || rightOpen || createOpen || postOpen;

  return (
    <>
      {/* Auth Gate */}
      {showAuthGate && (
        <SellerAuthGate
          action={`post a ${pendingCategory || 'listing'}`}
          onClose={() => { setShowAuthGate(false); setPendingCategory(null); }}
          onAuthenticated={() => { setShowAuthGate(false); setPostCategory(pendingCategory); setPendingCategory(null); }}
        />
      )}

      {/* Post Product Modal */}
      {postOpen && postCategory && (
        <PostProductModal
          category={postCategory}
          onClose={() => { setPostOpen(false); setPostCategory(null); }}
        />
      )}

      {/* Backdrop */}
      {anyOpen && (
        <div
          className="fixed inset-0 z-40 lg:hidden"
          style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)' }}
          onClick={closeAll}
        />
      )}

      {/* Create Menu Panel (centre + button) */}
      {createOpen && (
        <div
          className="fixed bottom-24 left-1/2 -translate-x-1/2 z-50 lg:hidden w-full max-w-sm"
          style={{ maxHeight: '75vh', overflowY: 'auto', animation: 'slideUp 0.2s ease-out' }}
          onClick={e => e.stopPropagation()}
        >
          <CreateMenu onClose={closeAll} />
        </div>
      )}

      {/* Right Roll-Up Panel — Realistic Icon Grid */}
      {rightOpen && (
        <div
          className="fixed bottom-24 right-3 z-50 lg:hidden rounded-2xl p-3"
          style={{ background: 'var(--card)', border: '1px solid var(--border)', boxShadow: '0 8px 32px rgba(0,0,0,0.5)', animation: 'slideUp 0.18s ease-out', width: '260px', maxHeight: '420px', overflowY: 'auto' }}
        >
          <div className="flex items-center justify-between mb-2 px-1">
            <span className="text-[10px] font-800 tracking-widest" style={{ color: 'var(--muted-foreground)' }}>SERVICES</span>
            <button onClick={() => setRightOpen(false)} className="w-5 h-5 rounded-md flex items-center justify-center" style={{ background: 'var(--secondary)' }}>
              <X size={10} style={{ color: 'var(--muted-foreground)' }} />
            </button>
          </div>
          <div className="grid grid-cols-4 gap-2">
            {rightModules.map((mod) => {
              const active = isActive(mod.href);
              return (
                <button
                  key={mod.id}
                  onClick={() => handleModuleNav(mod.href)}
                  className="relative flex flex-col items-center gap-1 py-2 px-1 rounded-xl transition-all duration-150 active:scale-95"
                  style={{
                    background: active ? 'rgba(0,200,83,0.12)' : 'var(--secondary)',
                    border: `1px solid ${active ? 'var(--primary)' : 'var(--border)'}`,
                  }}
                >
                  <div className="w-10 h-10 rounded-xl overflow-hidden">
                    <img src={mod.img} alt={mod.label} className="w-full h-full object-cover" />
                  </div>
                  <span className="text-[8px] font-600 text-center leading-tight" style={{ color: active ? 'var(--primary)' : 'var(--foreground)' }}>
                    {mod.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Left Nav Panel (main menu with realistic images) */}
      {leftOpen && (
        <div
          className="fixed bottom-24 left-3 z-50 lg:hidden rounded-2xl p-3 w-64"
          style={{ background: 'var(--card)', border: '1px solid var(--border)', boxShadow: '0 8px 32px rgba(0,0,0,0.5)', animation: 'slideUp 0.18s ease-out' }}
        >
          <div className="flex items-center justify-between mb-2 px-1">
            <span className="text-[10px] font-800 tracking-widest" style={{ color: 'var(--muted-foreground)' }}>MAIN MENU</span>
            <button onClick={() => setLeftOpen(false)} className="w-5 h-5 rounded-md flex items-center justify-center" style={{ background: 'var(--secondary)' }}>
              <X size={10} style={{ color: 'var(--muted-foreground)' }} />
            </button>
          </div>
          <div className="grid grid-cols-3 gap-1.5">
            {leftModules.map((mod) => {
              const active = isActive(mod.href);
              return (
                <button
                  key={mod.id}
                  onClick={() => handleModuleNav(mod.href)}
                  className="relative flex flex-col items-center gap-1 py-2 px-1 rounded-xl transition-all duration-150 active:scale-95"
                  style={{
                    background: active ? 'rgba(0,200,83,0.12)' : 'var(--secondary)',
                    border: `1px solid ${active ? 'var(--primary)' : 'var(--border)'}`,
                  }}
                >
                  <div className="w-10 h-10 rounded-xl overflow-hidden">
                    <img src={mod.img} alt={mod.label} className="w-full h-full object-cover" />
                  </div>
                  <span className="text-[8px] font-600 text-center leading-tight" style={{ color: active ? 'var(--primary)' : 'var(--foreground)' }}>
                    {mod.label}
                  </span>
                  {mod.badge ? (
                    <span className="absolute -top-1 -right-1 min-w-[14px] h-[14px] px-0.5 rounded-full text-[8px] font-700 flex items-center justify-center"
                      style={{ background: 'var(--danger)', color: '#fff' }}>
                      {mod.badge}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Pill-shaped Bottom Bar */}
      <nav
        className="fixed bottom-3 left-1/2 -translate-x-1/2 z-50 lg:hidden"
        style={{ width: 'calc(100% - 24px)', maxWidth: '420px' }}
      >
        <div
          className="flex items-center justify-between px-3 py-2.5 rounded-2xl"
          style={{
            background: 'rgba(10,15,30,0.97)',
            backdropFilter: 'blur(20px)',
            border: '1px solid var(--border)',
            boxShadow: '0 4px 24px rgba(0,0,0,0.5)',
          }}
        >
          {/* Left button — main menu */}
          <button
            onClick={() => { setLeftOpen(!leftOpen); setRightOpen(false); setCreateOpen(false); }}
            className="flex flex-col items-center gap-1 px-3 py-1 rounded-xl transition-all duration-150 active:scale-95"
            style={{ background: leftOpen ? 'rgba(0,200,83,0.12)' : 'transparent' }}
          >
            <div className="w-6 h-6 flex flex-col justify-center gap-1">
              <span className="block h-0.5 w-5 rounded-full" style={{ background: leftOpen ? 'var(--primary)' : 'var(--muted-foreground)' }} />
              <span className="block h-0.5 w-4 rounded-full" style={{ background: leftOpen ? 'var(--primary)' : 'var(--muted-foreground)' }} />
              <span className="block h-0.5 w-5 rounded-full" style={{ background: leftOpen ? 'var(--primary)' : 'var(--muted-foreground)' }} />
            </div>
            <span className="text-[9px] font-600" style={{ color: leftOpen ? 'var(--primary)' : 'var(--muted-foreground)' }}>Menu</span>
          </button>

          {/* Centre + button — Create Menu */}
          <button
            onClick={() => { setCreateOpen(!createOpen); setLeftOpen(false); setRightOpen(false); }}
            className="relative flex items-center justify-center w-14 h-14 rounded-2xl shadow-lg transition-all duration-200 active:scale-95"
            style={{
              background: createOpen
                ? 'linear-gradient(135deg, #00C853, #00E676)'
                : 'linear-gradient(135deg, #c8102e, #ff1744)',
              boxShadow: createOpen
                ? '0 4px 20px rgba(0,200,83,0.5)'
                : '0 4px 20px rgba(200,16,46,0.5)',
              transform: createOpen ? 'rotate(45deg)' : 'rotate(0deg)',
            }}
            aria-label="Create post"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </button>

          {/* Right button — services grid */}
          <button
            onClick={() => { setRightOpen(!rightOpen); setLeftOpen(false); setCreateOpen(false); }}
            className="flex flex-col items-center gap-1 px-3 py-1 rounded-xl transition-all duration-150 active:scale-95"
            style={{ background: rightOpen ? 'rgba(0,200,83,0.12)' : 'transparent' }}
          >
            <div className="grid grid-cols-2 gap-0.5 w-5 h-5">
              {[0,1,2,3].map(i => (
                <span key={i} className="block rounded-sm w-2 h-2" style={{ background: rightOpen ? 'var(--primary)' : 'var(--muted-foreground)' }} />
              ))}
            </div>
            <span className="text-[9px] font-600" style={{ color: rightOpen ? 'var(--primary)' : 'var(--muted-foreground)' }}>Services</span>
          </button>
        </div>
      </nav>
    </>
  );
}